import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { XData } from '@core/utils';
import { node, string, bool, number } from 'prop-types';

// context
import { LabeledItemsGroupContext } from '../LabeledItemsGroup/LabeledItemsGroup';

export const LabeledItem = ({
  children,
  labelText,
  labelWidth,
  hideColon,
  hideLabel,
  required = false,
  reversed = false
}) => {
  const labelId = useRef(XData.generateId());
  const labelRef = useRef(null);

  const {
    subscribeToGroup,
    groupSubscribersIds,
    labelMaxWidth,
    groupReady
  } = useContext(LabeledItemsGroupContext);

  const hasSubscribedToGroup = useMemo(() => {
    if (!groupSubscribersIds) return true;

    return groupSubscribersIds.includes(labelId.current);
  }, [groupSubscribersIds]);

  useEffect(() => {
    if (!groupReady) return;

    if (hasSubscribedToGroup) return;

    if (hideLabel) return;

    if (reversed) return;

    subscribeToGroup({
      labelWidth: labelRef.current.clientWidth,
      labelId: labelId.current
    });
  }, [hasSubscribedToGroup, hideLabel, groupReady, reversed]);

  const getColsStyles = (style) => ({ gridTemplateColumns: style });

  const determineWrapperStyle = () => {
    if (hideLabel) return getColsStyles('auto');

    if (reversed) return getColsStyles('auto auto');

    if (subscribeToGroup && !hasSubscribedToGroup) {
      return getColsStyles('auto 1fr');
    }

    if (subscribeToGroup) {
      return getColsStyles(`${labelMaxWidth}px auto`);
    }

    if (labelWidth) return getColsStyles(`${labelWidth}px auto`);

    return null;
  };

  const showLabel = useMemo(() => {
    if (!hideLabel) return true;

    return false;
  }, [hideLabel, labelMaxWidth]);

  return (
    <div
      className={`labeled-item ${
        reversed && showLabel ? 'labeled-item--reversed' : ''
      }`}
      style={determineWrapperStyle()}
    >
      {showLabel && (
        <div className="labeled-item__label" ref={labelRef}>
          <span>
            {labelText}
            {hideColon || reversed ? '' : ':'}
          </span>
          {required && <span className="labeled-item-required">*</span>}
        </div>
      )}
      {children}
    </div>
  );
};

LabeledItem.propTypes = {
  labelText: string.isRequired,
  labelWidth: number,
  hideColon: bool,
  hideLabel: bool,
  children: node,
  reversed: bool,
  required: bool
};
